import { useQuery } from '@tanstack/react-query'
import AiSparkleNavIcon from 'ext-ui/svg/icon/AiSparkleNavIcon'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectNotificationsUnreadCountFromState } from 'src/app/Notifications/redux/notifications.selectors'
import { SidenavContentExpandedItemLinkProps } from 'src/components/SidenavContentExpandedItemLink/SidenavContentExpandedItemLink'
import InsightsActive from 'src/components/Svgs/InsightsActive'
import InsightsInactive from 'src/components/Svgs/InsightsInactive'
import SvgSidenavAccountingActive from 'src/components/Svgs/SvgSidenavAccountingActive'
import SvgSidenavAccountingInactive from 'src/components/Svgs/SvgSidenavAccountingInactive'
import SvgSidenavBudgetsActive from 'src/components/Svgs/SvgSidenavBudgetsActive'
import SvgSidenavBudgetsInactive from 'src/components/Svgs/SvgSidenavBudgetsInactive'
import SvgSidenavCardsActive from 'src/components/Svgs/SvgSidenavCardsActive'
import SvgSidenavCardsInactive from 'src/components/Svgs/SvgSidenavCardsInactive'
import SvgSidenavExpensesActive from 'src/components/Svgs/SvgSidenavExpensesActive'
import SvgSidenavExpensesInactive from 'src/components/Svgs/SvgSidenavExpensesInactive'
import SvgSidenavHomeActive from 'src/components/Svgs/SvgSidenavHomeActive'
import SvgSidenavHomeInactive from 'src/components/Svgs/SvgSidenavHomeInactive'
import SvgSidenavManagerReviewActive from 'src/components/Svgs/SvgSidenavManagerReviewActive'
import SvgSidenavManagerReviewInactive from 'src/components/Svgs/SvgSidenavManagerReviewInactive'
import SvgSidenavNotificationsActive from 'src/components/Svgs/SvgSidenavNotificationsActive'
import SvgSidenavNotificationsInactive from 'src/components/Svgs/SvgSidenavNotificationsInactive'
import SvgSidenavPeopleActive from 'src/components/Svgs/SvgSidenavPeopleActive'
import SvgSidenavPeopleInactive from 'src/components/Svgs/SvgSidenavPeopleInactive'
import SvgSidenavWalletActive from 'src/components/Svgs/SvgSidenavWalletActive'
import SvgSidenavWalletInactive from 'src/components/Svgs/SvgSidenavWalletInactive'
import { OrganizationRoles } from 'src/enums'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import { NavGroup } from 'src/navigation/sidenav/types'
import {
  accountingReviewReportsUrl,
  accountsAccountsUrl,
  activityUrl,
  billPayUrl,
  budgetsUrl,
  expensesExpenseReportsUrl,
  expensesTransactionsUrl,
  homeUrl,
  insightsUrl,
  managerReviewReimbursementsURL,
  managerReviewTransactionsURL,
  myWalletBudgetsURL,
  myWalletPhysicalCardsUrl,
  myWalletTransactionsURL,
  myWalletVirtualCardsUrl,
  paymentsUrl,
  peopleUrl,
  reimbursementsUrl,
  reportingUrl,
  streamingAgentUrl,
  submitExpensesUrl,
  vendorsUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import {
  useFetchNotificationsCount,
  useFetchTasksCount,
  useIsTaskCountEnabled,
} from 'src/queries/activity.queries'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectIsAIInsightsEnabled,
  selectIsBetaOrgAgent,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { canManageBillPay as resolveCanManageBillPay } from 'src/utils/billPayAccess'
import {
  isBillPayM2VendorsEnabled,
  isBillPayM3PaymentsEnabled,
  isSpendReportingDashboardEnabled,
} from 'src/utils/featureFlags'

export interface UseMainNavItemsOptions {
  isSidenavOpen: boolean
  onNotificationClick?: () => void
  onActivityClick?: () => void
}

export interface UseMainNavItemsResult {
  navGroups: NavGroup[]
}

export const useMainNavItems = (
  options: UseMainNavItemsOptions,
): UseMainNavItemsResult => {
  const { isSidenavOpen, onNotificationClick, onActivityClick } = options

  const translate = useTranslate()
  const { data: currentUser } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentUser?.organizationId,
  })

  const {
    VIEW_BILL_PAY_SECTION: enableViewBillPayFeature,
    REGISTER_ACCOUNT: canRegisterAccount,
    VIEW_REPORTING: canViewReporting,
    VIEW_PAYABLES: canViewPayables,
    ORGANIZATION_USER_STATISTICS: orgUserStatisticsEnabled,
  } = useQueryUserFeatures([
    UserFeature.VIEW_BILL_PAY_SECTION,
    UserFeature.REGISTER_ACCOUNT,
    UserFeature.VIEW_REPORTING,
    UserFeature.VIEW_PAYABLES,
    UserFeature.ORGANIZATION_USER_STATISTICS,
  ]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const showAIInsights = useSelector(selectIsAIInsightsEnabled)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const reportingEnabled = isSpendReportingDashboardEnabled(
    remoteConfig,
    currentUser?.organizationId,
  )
  const billPayVendorsEnabled = isBillPayM2VendorsEnabled(
    remoteConfig,
    currentUser?.organizationId,
  )
  const billPayPaymentsEnabled = isBillPayM3PaymentsEnabled(
    remoteConfig,
    currentUser?.organizationId,
  )
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so this
  // section and the Bank Account settings nav/page can never diverge — see
  // src/utils/billPayAccess.
  const canManageBillPay = resolveCanManageBillPay({
    hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
    organizationRole: currentUser?.organizationRole,
    orgUserStatisticsEnabled,
  })

  const showExtendAI = useSelector(selectIsBetaOrgAgent)

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: currentUser?.organizationId,
    userId: currentUser?.id,
  })

  const { data: organizationTier, isFetched: saasTierIsFetched } =
    useQueryOrganizationSaasTier({
      orgId: currentUser?.organizationId,
      enabled: !!currentUser?.organizationId,
    })

  const { data: notificationsCount, isFetched: notificationsCountFetched } =
    useFetchNotificationsCount()
  const { data: tasksCount, isFetched: tasksCountFetched } =
    useFetchTasksCount()
  const isTaskCountEnabled = useIsTaskCountEnabled()

  // Selectors for business logic calculations
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: hasOutOfPocketExpenses } = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    hasOutOfPocketExpenses,
  )

  // Five nav-item stats. Legacy fetched three via SidenavWrapper's dispatch;
  // DelegateCount and SourceBudgetsEnabledCanIssueBudgetsCount free-rode on
  // other pages having dispatched them, so the Budgets entries could appear
  // late or never — fetching them here makes the nav deterministic.
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsSourceCount,
      StatisticsKeys.CreditCardsDelegateCount,
      StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsUserOrViewerMeCount,
    ] as const,
  })
  const creditCardsSourceCount =
    statisticsResults[StatisticsKeys.CreditCardsSourceCount].data ?? 0
  const creditCardsDelegateCount =
    statisticsResults[StatisticsKeys.CreditCardsDelegateCount].data ?? 0
  const creditCardsActiveCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const creditCardsUserOrViewerMeCount =
    statisticsResults[StatisticsKeys.CreditCardsUserOrViewerMeCount].data ?? 0
  const unreadNotificationsCount = useSelector(
    selectNotificationsUnreadCountFromState,
  )

  // Business logic calculations (moved from SidenavWrapper)
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestRole =
    currentUser?.organizationRole === OrganizationRoles.guest && showGuestRole

  const isTierProOrEnterprise =
    organizationTier?.tier === 'PRO' || organizationTier?.tier === 'ENTERPRISE'

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const showManagerReviewContent =
    directReports !== undefined &&
    directReports.length > 0 &&
    managerReviewRulesEnabled

  let activityCount = undefined
  if (isTaskCountEnabled && notificationsCountFetched && tasksCountFetched) {
    activityCount = (notificationsCount || 0) + (tasksCount || 0)
  } else if (!isTaskCountEnabled && notificationsCountFetched) {
    activityCount = notificationsCount || 0
  }

  const hasCreditCards =
    creditCardsUserOrViewerMeCount > 0 ||
    creditCardsActiveCanIssueVirtualCardsCount > 0
  const hasSourceCreditCards = creditCardsSourceCount > 0
  const hasDelegateCreditCards = creditCardsDelegateCount > 0

  const showMyBudgets = hasDelegateCreditCards
  const showBudgets =
    (creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 ||
      currentUser?.hasOrgBookkeeperPermissions) &&
    !isGuestRole &&
    hasVirtualCardFeatures

  const showCardTransactions =
    currentUser?.hasOrgAdminPermissions ||
    currentUser?.hasOrgBookkeeperPermissions ||
    currentUser?.hasOrgCardManagerPermissions ||
    showManagerReviewContent ||
    hasCreditCards

  const showCards =
    (hasCreditCards ||
      currentUser?.hasOrgBookkeeperPermissions ||
      showManagerReviewContent) &&
    !isGuestRole

  const showPhysicalCards = canRegisterAccount || hasSourceCreditCards

  const showPeople =
    !isGuestRole &&
    (organization?.memberListVisible ||
      currentUser?.hasOrgCardManagerPermissions)

  const showAccounting =
    currentUser?.hasOrgBookkeeperPermissions &&
    ((!organizationTier && saasTierIsFetched) ||
      organizationTier?.tier !== 'STARTER')

  const showAccountingReimbursements =
    (isTierProOrEnterprise || outOfPocketExpensesEnabled) &&
    currentUser?.hasOrgBookkeeperPermissions

  const showManagerReviewReimbursements =
    (isTierProOrEnterprise || outOfPocketExpensesEnabled) &&
    showManagerReviewContent

  const showExpensesSubmitExpenses =
    isTierProOrEnterprise || outOfPocketExpensesEnabled

  const showReporting = !!reportingEnabled && canViewReporting === true

  const { location: appLocation } = useExtendLocation()

  const walletExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = []
    if (hasVirtualCardFeatures || canRegisterAccount) {
      optionsArr.push({
        label: translate('Cards'),
        to: canRegisterAccount
          ? myWalletPhysicalCardsUrl()
          : myWalletVirtualCardsUrl(),
        isActive: [
          AppLocation.MyWalletVirtualCards,
          AppLocation.MyWalletVirtualCardDetail,
          AppLocation.MyWalletPhysicalCards,
        ].includes(appLocation),
        id: 'mainNavMyWalletCards',
        isOpen: isSidenavOpen,
      })
    }

    if (showMyBudgets) {
      optionsArr.push({
        id: 'mainNavMyWalletBudgets',
        label: translate('Budgets'),
        to: myWalletBudgetsURL(),
        isActive: appLocation === AppLocation.MyWalletBudgets,
        isOpen: isSidenavOpen,
      })
    }

    optionsArr.push({
      label: translate('Transactions'),
      to: myWalletTransactionsURL(),
      isActive: appLocation === AppLocation.MyWalletTransactions,
      id: 'mainNavMyWalletTransactions',
      isOpen: isSidenavOpen,
    })

    if (outOfPocketExpensesEnabled && showExpensesSubmitExpenses) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: submitExpensesUrl(),
          isActive: [
            AppLocation.MyWalletReimbursements,
            AppLocation.MyWalletReimbursementDetail,
          ].includes(appLocation),
          id: 'mainNavMyWalletReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    showExpensesSubmitExpenses,
    outOfPocketExpensesEnabled,
    appLocation,
    showMyBudgets,
    isSidenavOpen,
    translate,
    hasVirtualCardFeatures,
    canRegisterAccount,
  ])

  const cardExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = []

    if (hasVirtualCardFeatures) {
      optionsArr.push({
        label: translate('Virtual Cards'),
        to: virtualCardsUrl(),
        isActive: [
          AppLocation.VirtualCardDetail,
          AppLocation.VirtualCardsAll,
        ].includes(appLocation),
        id: 'mainNavVirtualCards',
        isOpen: isSidenavOpen,
      })
    }

    if (showPhysicalCards) {
      optionsArr = [
        {
          label: translate('Physical Cards'),
          to: accountsAccountsUrl(),
          isActive: [
            AppLocation.AccountOverview,
            AppLocation.AccountBudgets,
            AppLocation.AccountVirtualCards,
            AppLocation.AccountTransactions,
            AppLocation.AccountBudgetHistory,
            AppLocation.AccountSettings,
            AppLocation.AccountStatements,
            AppLocation.AccountActivity,
            AppLocation.PhysicalCards,
          ].includes(appLocation),
          id: 'mainNavPhysicalCards',
          isOpen: isSidenavOpen,
        },
        ...optionsArr,
      ]
    }

    if (enableViewBillPayFeature) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Bill Pay Cards'),
          to: billPayUrl(),
          isActive: [
            AppLocation.BillPayAll,
            AppLocation.BillPayDetail,
          ].includes(appLocation),
          id: 'mainNavBillPayCards',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    showPhysicalCards,
    enableViewBillPayFeature,
    appLocation,
    isSidenavOpen,
    translate,
    hasVirtualCardFeatures,
  ])

  // Bill Pay (M2+) — the prototype's standalone "Bill Pay" nav section
  // (InvoicePayNav): Payments (M3) and Vendors (M2), in the prototype's order.
  // Tier/role gate mirrors the Bank Account settings surface: VIEW_PAYABLES +
  // the shared `canManageBillPay` helper (owner/admin + full-visibility
  // bookkeepers, EX-40473), so this section and the Bank Account nav/page can
  // never disagree. Launch gate = the whole-feature Bill Pay gate
  // (`bill_pay` OR the `bill_pay_allowlist` beta list) AND each milestone's own
  // `bill_pay_mX` "is ready" bool — milestones stay independently releasable
  // (PR #7338 review), with ONE beta list across all milestones. Each item is
  // gated by its OWN bool and the section shows when ANY of them is on, so M3
  // can go dark without taking M2's Vendors link down with it.
  const billPayExpandOptions =
    useMemo((): SidenavContentExpandedItemLinkProps[] => {
      const options: SidenavContentExpandedItemLinkProps[] = []

      if (billPayPaymentsEnabled) {
        options.push({
          label: translate('Payments'),
          to: paymentsUrl(),
          isActive: appLocation === AppLocation.Payments,
          id: 'mainNavBillPayPayments',
          isOpen: isSidenavOpen,
        })
      }

      if (billPayVendorsEnabled) {
        options.push({
          label: translate('Vendors'),
          to: vendorsUrl(),
          isActive: appLocation === AppLocation.Vendors,
          id: 'mainNavBillPayVendors',
          isOpen: isSidenavOpen,
        })
      }

      return options
    }, [
      appLocation,
      billPayPaymentsEnabled,
      billPayVendorsEnabled,
      isSidenavOpen,
      translate,
    ])
  const showBillPaySection =
    (billPayVendorsEnabled || billPayPaymentsEnabled) &&
    canViewPayables &&
    canManageBillPay

  const managerReviewExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = [
      {
        label: translate('Card Transactions'),
        to: managerReviewTransactionsURL(),
        isActive: appLocation === AppLocation.ManagerReviewTransactions,
        id: 'mainNavManagerReviewCardTransactions',
        isOpen: isSidenavOpen,
      },
    ]

    if (outOfPocketExpensesEnabled && showManagerReviewReimbursements) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: managerReviewReimbursementsURL(),
          isActive:
            appLocation === AppLocation.ManagerReviewReimbursements ||
            appLocation === AppLocation.ManagerReviewReimbursementDetail,
          id: 'mainNavManagerReviewReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    outOfPocketExpensesEnabled,
    appLocation,
    isSidenavOpen,
    showManagerReviewReimbursements,
    translate,
  ])

  const accountingExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = [
      {
        label: translate('Card Transactions'),
        to: accountingReviewReportsUrl(),
        isActive: [AppLocation.AccountingReviewReports].includes(appLocation),
        id: 'mainNavAccountingCardTransactions',
        isOpen: isSidenavOpen,
      },
    ]

    if (outOfPocketExpensesEnabled && showAccountingReimbursements) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: reimbursementsUrl(),
          isActive: [
            AppLocation.AccountingReimbursements,
            AppLocation.ExpenseReport,
          ].includes(appLocation),
          id: 'mainNavAccountingReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    optionsArr = [
      ...optionsArr,
      {
        label: translate('Statements'),
        to: expensesExpenseReportsUrl(),
        isActive: [
          AppLocation.AccountingExpenseReports,
          AppLocation.OrgExpenseReportsHistoricalReports,
          AppLocation.OrgExpenseReport,
        ].includes(appLocation),
        id: 'mainNavAccountingStatements',
        isOpen: isSidenavOpen,
      },
    ]

    return optionsArr
  }, [
    outOfPocketExpensesEnabled,
    appLocation,
    isSidenavOpen,
    showAccountingReimbursements,
    translate,
  ])

  const navGroups = useMemo(
    (): NavGroup[] => [
      // Spender
      {
        items: [
          {
            id: 'mainNavHome',
            label: translate('Home'),
            to: homeUrl(),
            isActive: appLocation === AppLocation.Home,
            activeSvg: <SvgSidenavHomeActive />,
            inactiveSvg: <SvgSidenavHomeInactive />,
          },
          {
            id: 'manNavActivity',
            label: translate('Activity'),
            to: activityUrl(),
            isActive: appLocation === AppLocation.Activity,
            activeSvg: <SvgSidenavNotificationsActive />,
            inactiveSvg: <SvgSidenavNotificationsInactive />,
            notificationsCount: activityCount,
            onClick: onActivityClick,
          },
          ...(showExtendAI
            ? [
                {
                  id: 'mainNavStreamingAgent',
                  label: translate('AI Agent'),
                  to: streamingAgentUrl(),
                  isActive: appLocation === AppLocation.StreamingAgent,
                  activeSvg: <AiSparkleNavIcon fill="var(--color-cta)" />,
                  inactiveSvg: <AiSparkleNavIcon />,
                },
              ]
            : []),
          {
            id: 'mainNavMyWallet',
            label: translate('My Wallet'),
            isActive: [
              AppLocation.MyWalletVirtualCards,
              AppLocation.MyWalletVirtualCardDetail,
              AppLocation.MyWalletPhysicalCards,
              AppLocation.MyWalletTransactions,
              AppLocation.MyWalletBudgets,
              AppLocation.MyWalletReimbursements,
              AppLocation.MyWalletReimbursementDetail,
            ].includes(appLocation as AppLocation),
            activeSvg: <SvgSidenavWalletActive />,
            inactiveSvg: <SvgSidenavWalletInactive />,
            expandOptions: walletExpandOptions,
          },
        ],
        condition: true,
      },
      // Manager
      {
        items: [
          ...(showManagerReviewContent
            ? [
                {
                  id: 'mainNavManagerReview',
                  label: translate('Manager Review'),
                  isActive: [
                    AppLocation.ManagerReviewTransactions,
                    AppLocation.ManagerReviewReimbursements,
                    AppLocation.ManagerReviewReimbursementDetail,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavManagerReviewActive />,
                  inactiveSvg: <SvgSidenavManagerReviewInactive />,
                  expandOptions: managerReviewExpandOptions,
                },
              ]
            : []),
          ...(showCards
            ? [
                {
                  id: 'mainNavCards',
                  label: translate('Cards'),
                  isActive: [
                    AppLocation.VirtualCardsAll,
                    AppLocation.VirtualCardDetail,
                    AppLocation.PhysicalCards,
                    AppLocation.BillPayAll,
                    AppLocation.AccountOverview,
                    AppLocation.AccountBudgets,
                    AppLocation.AccountVirtualCards,
                    AppLocation.AccountTransactions,
                    AppLocation.AccountBudgetHistory,
                    AppLocation.AccountSettings,
                    AppLocation.AccountStatements,
                    AppLocation.AccountActivity,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavCardsActive />,
                  inactiveSvg: <SvgSidenavCardsInactive />,
                  expandOptions: cardExpandOptions,
                },
              ]
            : []),
          // Icon: the prototype's InvoicePayNav.tsx uses `ExpensesNew` for
          // this group — the SAME glyph it uses for Card Transactions just
          // below. Matching that reuse deliberately, not guessing: Card
          // Transactions here already renders SvgSidenavExpensesActive/
          // Inactive, so Bill Pay reuses that exact pair too.
          ...(showBillPaySection
            ? [
                {
                  id: 'mainNavBillPay',
                  label: translate('Bill Pay'),
                  isActive: [
                    AppLocation.Payments,
                    AppLocation.Vendors,
                  ].includes(appLocation),
                  activeSvg: <SvgSidenavExpensesActive />,
                  inactiveSvg: <SvgSidenavExpensesInactive />,
                  expandOptions: billPayExpandOptions,
                },
              ]
            : []),
          ...(showBudgets
            ? [
                {
                  id: 'mainNavBudgets',
                  label: translate('Budgets'),
                  to: budgetsUrl(),
                  isActive: [
                    AppLocation.BudgetsAll,
                    AppLocation.BudgetsNeedsApproval,
                    AppLocation.BudgetDetail,
                    AppLocation.BudgetOverview,
                    AppLocation.BudgetVirtualCards,
                    AppLocation.BudgetTransactions,
                    AppLocation.BudgetHistory,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavBudgetsActive />,
                  inactiveSvg: <SvgSidenavBudgetsInactive />,
                },
              ]
            : []),
          ...(showCardTransactions
            ? [
                {
                  id: 'mainNavCardTransactions',
                  label: translate('Card Transactions'),
                  to: expensesTransactionsUrl(),
                  isActive: appLocation === AppLocation.CardTransactions,
                  activeSvg: <SvgSidenavExpensesActive />,
                  inactiveSvg: <SvgSidenavExpensesInactive />,
                },
              ]
            : []),
        ],
        showDivider: true,
        condition:
          showCards ||
          showBudgets ||
          showCardTransactions ||
          showManagerReviewContent,
      },
      // Accountant
      {
        items: [
          ...(showAccounting
            ? [
                {
                  id: 'mainNavAccounting',
                  label: translate('Accounting'),
                  isActive: [
                    AppLocation.AccountingReviewReports,
                    AppLocation.AccountingReimbursements,
                    AppLocation.AccountingExpenseReports,
                    AppLocation.OrgExpenseReportsHistoricalReports,
                    AppLocation.OrgExpenseReport,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavAccountingActive />,
                  inactiveSvg: <SvgSidenavAccountingInactive />,
                  expandOptions: accountingExpandOptions,
                },
              ]
            : []),
          ...(showPeople
            ? [
                {
                  id: 'mainNavPeople',
                  label: translate('People'),
                  to: peopleUrl(),
                  isActive: [
                    AppLocation.PeopleMembers,
                    AppLocation.PeopleMembersDetail,
                    AppLocation.PeopleInvitedToOrg,
                    AppLocation.PeopleInvitedToOrgDetail,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavPeopleActive />,
                  inactiveSvg: <SvgSidenavPeopleInactive />,
                },
              ]
            : []),
          ...(showAIInsights
            ? [
                {
                  id: 'mainNavInsights',
                  label: translate('Insights'),
                  to: insightsUrl(),
                  isActive: appLocation === AppLocation.Insights,
                  activeSvg: <InsightsActive />,
                  inactiveSvg: <InsightsInactive />,
                },
              ]
            : []),
          ...(showReporting
            ? [
                {
                  id: 'mainNavReporting',
                  label: translate('Reporting'),
                  to: reportingUrl(),
                  isActive: appLocation === AppLocation.Reporting,
                  activeSvg: <InsightsActive />,
                  inactiveSvg: <InsightsInactive />,
                },
              ]
            : []),
        ],
        showDivider: true,
        condition:
          currentUser?.hasOrgBookkeeperPermissions ||
          showPeople ||
          showReporting,
      },
    ],
    [
      appLocation,
      translate,
      unreadNotificationsCount,
      activityCount,
      onNotificationClick,
      onActivityClick,
      walletExpandOptions,
      showManagerReviewContent,
      managerReviewExpandOptions,
      showCards,
      cardExpandOptions,
      showBillPaySection,
      billPayExpandOptions,
      showBudgets,
      showCardTransactions,
      currentUser?.hasOrgBookkeeperPermissions,
      accountingExpandOptions,
      showPeople,
      showAccounting,
      showAIInsights,
      showReporting,
      showExtendAI,
    ],
  )

  return { navGroups }
}
